import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import {
  fetchShortLinks,
  createShortLink,
  updateShortLink,
  deleteShortLink,
  fetchMerchants,
} from "../../utils/shortLinkCalls";
import { errorToast, successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const PAGE_SIZE = 20;

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    successToast("Copied to clipboard");
  } catch {
    errorToast("Copy failed — please copy manually");
  }
};

const emptyForm = { sourceType: "url", targetUrl: "", releaseRepo: "", releaseAssetName: "", merchantReference: "", label: "" };

const ShortLinkList = () => {
  const navigate = useNavigate();

  const [links, setLinks] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [merchants, setMerchants] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [newLink, setNewLink] = useState(null); // freshly-created link banner

  const load = useCallback(async () => {
    setIsLoading(true);
    const data = await fetchShortLinks({ page, limit: PAGE_SIZE, search: search || undefined });
    setLinks(data.rows || []);
    setTotalPages(data.totalPages || 1);
    setTotalCount(data.totalCount || 0);
    setIsLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => setMerchants(await fetchMerchants()))();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setNewLink(null);
    setCreateModal(true);
  };

  const handleCreate = async () => {
    const payload = {
      sourceType: form.sourceType,
      label: form.label.trim() || undefined,
      merchantReference: form.merchantReference || undefined,
    };
    if (form.sourceType === "github_latest") {
      if (!/^[\w.-]+\/[\w.-]+$/.test(form.releaseRepo.trim())) {
        errorToast('Enter the GitHub repo as "owner/name"');
        return;
      }
      payload.releaseRepo = form.releaseRepo.trim();
      payload.releaseAssetName = form.releaseAssetName.trim() || undefined;
    } else {
      if (!/^https?:\/\/.+/i.test(form.targetUrl.trim())) {
        errorToast("Enter a valid URL starting with http:// or https://");
        return;
      }
      payload.targetUrl = form.targetUrl.trim();
    }
    setSubmitting(true);
    const result = await createShortLink(payload);
    setSubmitting(false);
    if (result) {
      setCreateModal(false);
      setNewLink(result);
      load();
    }
  };

  const handleToggleActive = async (link) => {
    const updated = await updateShortLink(link.id, { active: !link.active });
    if (updated) setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, active: updated.active } : l)));
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete "${link.label || link.code}"? This removes its analytics too.`)) return;
    const ok = await deleteShortLink(link.id);
    if (ok) load();
  };

  const onSearchSubmit = (e) => { e.preventDefault(); setPage(1); load(); };

  const primaryBtn = { background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, padding: "8px 16px" };
  const ghostBtn = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700 };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="App Links / QR" parent="App Links" />
      <Container fluid className="rpt-wrap">
        {/* Header */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <form onSubmit={onSearchSubmit} style={{ display: "flex", gap: 8, flex: 1, minWidth: 240 }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search label, code, URL or merchant…"
              style={{ borderRadius: 8, border: `1px solid ${C.border}`, maxWidth: 360, fontSize: 13 }}
            />
            <Button size="sm" type="submit" style={ghostBtn}>Search</Button>
          </form>
          <Button size="sm" style={primaryBtn} onClick={openCreate}>+ Create Short Link</Button>
        </div>

        {/* Newly-created link banner */}
        {newLink && (
          <RCard style={{ borderLeft: `4px solid ${C.green}`, marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <img src={newLink.qrUrl} alt="QR code" width={120} height={120} style={{ borderRadius: 10, border: `1px solid ${C.border}`, padding: 6, background: "#fff" }} />
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>Short link created — share it with the client</div>
                <code style={{ fontSize: 14, background: C.bg, padding: "8px 14px", borderRadius: 8, display: "inline-block", fontWeight: 600, border: `1px solid ${C.borderLight}`, wordBreak: "break-all" }}>{newLink.shortUrl}</code>
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <Button size="sm" style={ghostBtn} onClick={() => copy(newLink.shortUrl)}>Copy link</Button>
                  <a href={newLink.qrUrl + "?download=1"} download style={{ textDecoration: "none" }}>
                    <Button size="sm" style={ghostBtn}>Download QR</Button>
                  </a>
                  <a href={newLink.shortUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                    <Button size="sm" style={ghostBtn}>Open</Button>
                  </a>
                  <Button size="sm" style={{ ...ghostBtn, color: C.coral }} onClick={() => setNewLink(null)}>Dismiss</Button>
                </div>
              </div>
            </div>
          </RCard>
        )}

        {/* Table */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : links.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No short links yet — create one to get started.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl.table}>
                <thead>
                  <tr>
                    {["Label", "Merchant", "Short URL", "Target", "Views", "Downloads", "Status", "Created", "Actions"].map((h) => (
                      <th key={h} style={{ ...tbl.th, ...(h === "Actions" && { textAlign: "right" }) }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {links.map((l) => (
                    <tr key={l.id} className="rpt-row">
                      <td style={tbl.td}>{l.label || <span style={{ color: C.textLight }}>—</span>}</td>
                      <td style={tbl.td}>{l.merchantName || l.merchantReference || <span style={{ color: C.textLight }}>—</span>}</td>
                      <td style={tbl.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <code style={{ fontSize: 12, background: C.bg, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.borderLight}` }}>/s/{l.code}</code>
                          <Button size="sm" style={{ ...ghostBtn, padding: "2px 8px" }} onClick={() => copy(l.shortUrl)}>Copy</Button>
                        </div>
                      </td>
                      <td style={{ ...tbl.td, maxWidth: 280 }}>
                        {l.sourceType === "github_latest" ? (
                          <span title={`${l.releaseRepo}${l.releaseAssetName ? " • " + l.releaseAssetName : ""}`} style={{ fontSize: 12, color: C.coralDark, display: "inline-block", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
                            <Badge color={C.blue} bg={C.blueLight}>↻ latest</Badge>&nbsp;{l.releaseRepo}
                          </span>
                        ) : (
                          <a href={l.targetUrl} target="_blank" rel="noreferrer" title={l.targetUrl} style={{ color: C.coralDark, fontSize: 12, textDecoration: "none", display: "inline-block", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>{l.targetUrl}</a>
                        )}
                      </td>
                      <td style={tbl.td}><strong>{l.viewCount}</strong></td>
                      <td style={tbl.td}><strong>{l.downloadCount}</strong></td>
                      <td style={tbl.td}>
                        <span onClick={() => handleToggleActive(l)} style={{ cursor: "pointer" }} title="Click to toggle">
                          <Badge color={l.active ? C.green : C.coral} bg={l.active ? C.greenLight : C.coralLight}>{l.active ? "Active" : "Disabled"}</Badge>
                        </span>
                      </td>
                      <td style={tbl.td}>{new Date(l.createdAt).toLocaleDateString()}</td>
                      <td style={{ ...tbl.td, textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <Button size="sm" style={ghostBtn} onClick={() => navigate(`/short-links/edit/${l.id}`)}>Edit</Button>
                          <a href={l.qrUrl + "?download=1"} download style={{ textDecoration: "none" }}>
                            <Button size="sm" style={ghostBtn}>QR</Button>
                          </a>
                          <Button size="sm" style={{ ...ghostBtn, color: C.coral }} onClick={() => handleDelete(l)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </RCard>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 12 }}>
            <span style={{ fontSize: 12, color: C.textMed }}>{totalCount} links · page {page} of {totalPages}</span>
            <Button size="sm" style={ghostBtn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
            <Button size="sm" style={ghostBtn} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        )}
      </Container>

      {/* Create Modal */}
      <Modal isOpen={createModal} toggle={() => setCreateModal(false)}>
        <ModalHeader toggle={() => setCreateModal(false)}>Create Short Link</ModalHeader>
        <ModalBody>
          <Label>Link type *</Label>
          <Input
            type="select"
            value={form.sourceType}
            onChange={(e) => setForm((prev) => ({ ...prev, sourceType: e.target.value }))}
            style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}
          >
            <option value="url">Custom URL</option>
            <option value="github_latest">Latest GitHub release (auto-updates)</option>
          </Input>

          {form.sourceType === "github_latest" ? (
            <>
              <Label>GitHub repo *</Label>
              <Input
                type="text"
                value={form.releaseRepo}
                onChange={(e) => setForm((prev) => ({ ...prev, releaseRepo: e.target.value }))}
                placeholder="owner/name — e.g. ahmad71666/wags-pos-releases-namq"
                style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, fontSize: 13, fontFamily: "monospace" }}
              />
              <Label>Asset file name (optional)</Label>
              <Input
                type="text"
                value={form.releaseAssetName}
                onChange={(e) => setForm((prev) => ({ ...prev, releaseAssetName: e.target.value }))}
                placeholder="e.g. wags-pos.apk — leave blank for the first .apk"
                style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, fontSize: 13, fontFamily: "monospace" }}
              />
              <div style={{ fontSize: 11.5, color: C.textLight, marginTop: -6, marginBottom: 12 }}>
                This link always resolves to the latest release — no need to update it when you publish a new build. Name it in the Label (e.g. "Namq POS", "Namq Pickup").
              </div>
            </>
          ) : (
            <>
              <Label>Target URL *</Label>
              <Input
                type="text"
                value={form.targetUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
                placeholder="https://… (the APK / download link)"
                style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, fontSize: 13 }}
              />
            </>
          )}

          <Label>Merchant (optional)</Label>
          <Input
            type="select"
            value={form.merchantReference}
            onChange={(e) => setForm((prev) => ({ ...prev, merchantReference: e.target.value }))}
            style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}
          >
            <option value="">— None —</option>
            {merchants.map((m) => (
              <option key={m.id} value={m.reference}>{m.name} ({m.reference})</option>
            ))}
          </Input>
          <Label>Label (optional)</Label>
          <Input
            type="text"
            value={form.label}
            onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
            placeholder="e.g. Wags POS v2.3.1"
            style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
          />
        </ModalBody>
        <ModalFooter>
          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => setCreateModal(false)}>Cancel</Button>
          <Button disabled={submitting} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleCreate}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ShortLinkList;
